import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE from '../config';

const API = `${API_BASE}/admin`;

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' ' + new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export default function DocumentBrowser() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [expandedClient, setExpandedClient] = useState(null);
  const [expandedEmp, setExpandedEmp] = useState(null);

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/equipment-documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 401 || res.status === 403) { navigate('/admin'); return; }
      const data = await res.json();
      setClients(data.clients || data.departments || []);
    } catch (e) {
      setError('Cannot reach server. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleDownload = async (assignmentId, type = 'handover') => {
    try {
      const res = await fetch(`${API}/equipment-assignments/${assignmentId}/download?type=${type}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { alert('PDF not available on server disk.'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { URL.revokeObjectURL(url); document.body.removeChild(a); }, 1000);
    } catch (e) {
      alert('Failed to load PDF: ' + e.message);
    }
  };

  const lc = (s) => (s || '').toLowerCase();

  // Filter clients / employees by search
  const filtered = clients.map(client => {
    if (!search) return client;
    const clientMatch = lc(client.name).includes(lc(search));
    const filteredEmps = client.employees.filter(e =>
      clientMatch ||
      lc(e.employeeId).includes(lc(search)) ||
      lc(e.employeeName).includes(lc(search))
    );
    return { ...client, employees: filteredEmps };
  }).filter(c => c.employees.length > 0);

  const totalEmployees = clients.reduce((s, c) => s + c.employees.length, 0);
  const totalDocs = clients.reduce((s, c) => s + c.employees.reduce((ss, e) => ss + e.assignments.length, 0), 0);

  return (
    <div style={st.page}>
      <div style={st.container}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
          <button onClick={() => navigate('/admin')} className="btn-back-nav">← Back</button>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>📁 Document Browser</h1>
            <p style={{ margin: 0, color: '#666', fontSize: 13 }}>
              {clients.length} client{clients.length !== 1 ? 's' : ''} · {totalEmployees} employees · {totalDocs} protocols
            </p>
          </div>
          <button onClick={fetchDocs} style={st.refreshBtn}>↻ Refresh</button>
        </div>

        {/* Search */}
        <input
          type="text"
          placeholder="Search by Client, Employee ID or Name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={st.searchInput}
        />

        {error && (
          <div style={st.alertWarn}>
            ⚠️ {error}
            <button onClick={fetchDocs} style={{ marginLeft: 12, padding: '4px 10px', cursor: 'pointer' }}>Retry</button>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>No documents found.</div>
        ) : (
          /* Client Tiles Grid */
          <div style={st.clientGrid}>
            {filtered.map(client => {
              const isOpen = expandedClient === client.name;
              const totalProtos = client.employees.reduce((s, e) => s + e.assignments.length, 0);

              return (
                <div key={client.name} style={{ ...st.clientCard, ...(isOpen ? st.clientCardOpen : {}) }}>
                  {/* Client tile header */}
                  <div
                    onClick={() => setExpandedClient(isOpen ? null : client.name)}
                    style={st.clientHeader}
                  >
                    <div style={{ fontSize: 28 }}>🏢</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>{client.name}</div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>
                        {client.employees.length} employee{client.employees.length !== 1 ? 's' : ''} · {totalProtos} protocol{totalProtos !== 1 ? 's' : ''}
                      </div>
                    </div>
                    <span style={{ color: '#94a3b8', fontSize: 14 }}>{isOpen ? '▲' : '▼'}</span>
                  </div>

                  {/* Expanded: Employee ID tiles */}
                  {isOpen && (
                    <div style={st.empTileGrid}>
                      {client.employees.map(emp => {
                        const empKey = `${client.name}__${emp.employeeId}`;
                        const isEmpOpen = expandedEmp === empKey;

                        return (
                          <div key={empKey} style={{ ...st.empTile, ...(isEmpOpen ? st.empTileOpen : {}) }}>
                            {/* Employee tile header */}
                            <div
                              onClick={() => setExpandedEmp(isEmpOpen ? null : empKey)}
                              style={st.empTileHeader}
                            >
                              <div style={st.empAvatar}>
                                {(emp.employeeName?.[0] || '?').toUpperCase()}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{emp.employeeName}</div>
                                <div style={{ fontSize: 11, color: '#64748b' }}>ID: {emp.employeeId}</div>
                              </div>
                              <span style={st.protoBadge}>
                                {emp.assignments.length}
                              </span>
                            </div>

                            {/* Employee expanded: documents */}
                            {isEmpOpen && (
                              <div style={st.docList}>
                                {emp.assignments.length === 0 ? (
                                  <div style={{ padding: '10px 14px', color: '#94a3b8', fontSize: 12 }}>No documents.</div>
                                ) : (
                                  emp.assignments.map((a, idx) => (
                                    <div key={a.id} style={{ ...st.docRow, borderBottom: idx < emp.assignments.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                                      <span style={{ fontSize: 16 }}>📄</span>
                                      <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 3 }}>
                                          <span style={{ fontWeight: 600, fontSize: 12 }}>
                                            {a.equipmentType === 'new' ? 'New Equipment' : 'Replacement'} #{a.id}
                                          </span>
                                          {a.protocolSigned
                                            ? <span style={st.badgeSigned}>✔ Signed</span>
                                            : <span style={st.badgePending}>⏳ Pending</span>}
                                          {a.returned && <span style={st.badgeReturned}>Returned</span>}
                                        </div>
                                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{fmtDate(a.createdAt)}</div>
                                        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                                          <button onClick={() => handleDownload(a.id, a.equipmentType === 'change' ? 'replacement' : 'handover')} style={st.dlBtn}>{a.equipmentType === 'change' ? '🔄 Replacement' : '📄 Handover'}</button>
                                          {(a.returned || a.returnSupabaseDocUrl) && (
                                            <button onClick={() => handleDownload(a.id, 'return')} style={st.dlBtnReturn}>🔄 Return</button>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const st = {
  page: { minHeight: '100vh', background: '#f8fafc', padding: '24px 16px' },
  container: { maxWidth: 1100, margin: '0 auto' },
  backBtn: { padding: '8px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 },
  refreshBtn: { marginLeft: 'auto', padding: '8px 14px', background: '#667eea', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 },
  searchInput: { width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, marginBottom: 20, boxSizing: 'border-box' },
  alertWarn: { background: '#fff3cd', color: '#856404', border: '1px solid #ffc107', borderRadius: 8, padding: '12px 16px', marginBottom: 16 },

  // Client tiles grid
  clientGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 },
  clientCard: { background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', overflow: 'hidden', gridColumn: 'auto' },
  clientCardOpen: { gridColumn: '1 / -1' },
  clientHeader: { display: 'flex', alignItems: 'center', gap: 12, padding: '16px 18px', cursor: 'pointer', userSelect: 'none' },

  // Employee tiles inside expanded client
  empTileGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10, padding: '12px 14px 14px', borderTop: '1px solid #f1f5f9' },
  empTile: { background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden', transition: 'box-shadow 0.2s' },
  empTileOpen: { background: '#fff', border: '1px solid #cbd5e1', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', gridColumn: '1 / -1' },
  empTileHeader: { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', cursor: 'pointer', userSelect: 'none' },
  empAvatar: { width: 34, height: 34, borderRadius: '50%', background: '#6366f1', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem', fontWeight: 700, flexShrink: 0 },
  protoBadge: { background: '#e0e7ff', color: '#4338ca', borderRadius: 12, padding: '2px 9px', fontSize: 11, fontWeight: 700, minWidth: 20, textAlign: 'center' },

  // Document list inside expanded employee
  docList: { borderTop: '1px solid #e2e8f0' },
  docRow: { display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 14px' },
  badgeSigned: { background: '#dcfce7', color: '#15803d', borderRadius: 8, padding: '1px 7px', fontSize: 10, fontWeight: 600 },
  badgePending: { background: '#fef9c3', color: '#a16207', borderRadius: 8, padding: '1px 7px', fontSize: 10, fontWeight: 600 },
  badgeReturned: { background: '#f3e8ff', color: '#7c3aed', borderRadius: 8, padding: '1px 7px', fontSize: 10, fontWeight: 600 },
  dlBtn: { display: 'inline-flex', alignItems: 'center', gap: 3, background: '#2563eb', color: '#fff', padding: '4px 10px', borderRadius: 6, fontSize: 11, border: 'none', cursor: 'pointer', fontWeight: 600 },
  dlBtnReturn: { display: 'inline-flex', alignItems: 'center', gap: 3, background: '#7c3aed', color: '#fff', padding: '4px 10px', borderRadius: 6, fontSize: 11, border: 'none', cursor: 'pointer', fontWeight: 600 },
};
